import {
    StyleFunction,
    Surface,
    useAuth,
    useForm,
    useMe,
    useThemedStyle,
} from "@venuepos/react-common";
import { useIntegrationCreateMutation } from "graphql-sdk";
import { IIntegrationInput, schemaIntegrationInput } from "lib";
import React, { useCallback } from "react";
import { useTranslation } from "locales";
import { View } from "react-native";

import { useHandleMutationError } from "../../../hooks/use-handle-mutation-error";
import { RootStackScreenProps } from "../../../navigation";
import { defaultIntegration } from "../forms";
import { GeneralForm } from "../general-form";
import { IntegrationScreen } from "../integration-screen";

type ScreenProps = RootStackScreenProps<"INTEGRATION_EDIT">;

export function CreateIntegrationScreen({
    navigation: { navigate },
}: ScreenProps) {
    const auth = useAuth();
    auth.enforce("merchant.merchant_settings");

    const [t] = useTranslation();
    const [integrationCreate] = useIntegrationCreateMutation();
    const { handleMutationError } = useHandleMutationError();

    const styles = useThemedStyle(styleFunc);
    const form = useForm<IIntegrationInput>(
        schemaIntegrationInput,
        defaultIntegration
    );
    const [{ values }, { reset }] = form;

    const me = useMe();

    const create = useCallback(async () => {
        if (
            !values ||
            !me ||
            !me.merchant ||
            !me.merchant.id ||
            values.type === undefined
        ) {
            return;
        }

        // After creating a new integration, go to the primary configuration tab.
        // For "PARTNER-API" and "VENUE-INVENTORY", stay on tab 0 (there's no configuration)
        // For "FISKALY", go to tab 2 (Organization tab, 0-based)
        // For all else: Go to the tab 1 (Configuration tab)
        let goToTab =
            values.type === "FISKALY"
                ? 2
                : values.type === "PARTNER-API" ||
                  values.type === "VENUE-INVENTORY"
                ? 0
                : 1;
        let createResponse;

        await handleMutationError(
            async () => {
                createResponse = await integrationCreate({
                    variables: {
                        integration: {
                            merchantId: me.merchant!.id,
                            type: values.type!,
                            active: values.active,
                            name: values.name,
                            apiKey: values.apiKey,
                        },
                    },
                });
            },
            t("common.created", "Created"),
            () => {
                reset();
                navigate("INTEGRATION_EDIT", {
                    id: createResponse.data.integrationCreate.id,

                    tabIndex: goToTab,
                });
            }
        );
    }, [
        values,
        me,
        handleMutationError,
        t,
        integrationCreate,
        reset,
        navigate,
    ]);

    return (
        <IntegrationScreen>
            <Surface>
                <View style={styles.container}>
                    <GeneralForm
                        form={form}
                        onSubmit={create}
                        submitButton={["common.create", "Create"]}
                        pickerDisabled={false}
                    />
                </View>
            </Surface>
        </IntegrationScreen>
    );
}

const styleFunc: StyleFunction = theme => ({
    container: {
        width: "100%",
        maxWidth: theme.dimensions.maxFormWidth,
        alignSelf: "flex-start",
    },
});
